import {
  Alert,
  Button,
  Card,
  Checkbox,
  Separator,
  HStack,
  Stack,
  Text,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useState, useMemo } from "react";
import { codingExport } from "../../../../survey/utils/export-coding-csv";
import { treatmentExport } from "../../../../survey/utils/export-treatment-csv";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";

const i18prefix = "map:export";

enum ExportType {
  Coding = "Coding",
  Treatments = "Treatments",
}

interface Props {
  onClose: () => void;
}
const CsvExportModalBase: React.FC<Props> = ({ onClose }) => {
  const {
    craNodeStore,
    craForecastStore,
    filterStore,
    craWayStore,
    surveyStore,
  } = useStores();

  const selectedSurvey = surveyStore.getSelected();

  const waysWithNodes = useMemo(() => {
    return filterStore.originalOrForecastedCraWays.filter(
      (way) => craNodeStore.nodesByWayId.get(way.id)?.length
    );
    // eslint-disable-next-line
  }, [craWayStore.ways]);

  const [selectedCraWayIds, setSelectedCraWayIds] = useState<string[]>(() =>
    waysWithNodes.map((way) => way.id)
  );
  const [exportType, setExportType] = useState<ExportType>();

  const submit = () => {
    switch (exportType) {
      case ExportType.Coding: {
        codingExport(selectedCraWayIds, craForecastStore.computedValues);
        break;
      }
      case ExportType.Treatments: {
        const waystoExport = waysWithNodes.filter((way) =>
          selectedCraWayIds.includes(way.id)
        );
        treatmentExport(
          selectedSurvey!,
          waystoExport,
          craForecastStore.computedValues?.craNodes ?? craNodeStore.nodesByWayId
        );
        break;
      }
    }

    onClose();
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`map:header.export`)}
      footer={
        <HStack>
          <Button colorPalette="primary" variant="ghost" onClick={onClose}>
            {t(`${i18prefix}.close`)}
          </Button>
          <Button
            colorPalette="primary"
            onClick={submit}
            disabled={!selectedCraWayIds.length || !exportType}
          >
            {t(`${i18prefix}.export`)}
          </Button>
        </HStack>
      }
    >
      <Stack gap={4}>
        {craForecastStore.isForecastModeActivated() && (
          <Card.Root variant="outline">
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Text fontSize="sm">{t(`${i18prefix}.forecastWarning`)}</Text>
            </Alert.Root>
          </Card.Root>
        )}
        <NativeSelect.Root>
          <NativeSelect.Field
            placeholder={t(`${i18prefix}.selectDefault`)}
            value={exportType}
            onChange={(e) => {
              const { value } = e.target;
              setExportType(value as ExportType);
            }}
          >
            {Object.values(ExportType).map((type) => (
              <option value={type} key={type}>
                {type}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        <Checkbox.Root
          colorPalette="primary"
          checked={selectedCraWayIds.length === waysWithNodes.length}
          onCheckedChange={({ checked }) => {
            if (checked) {
              setSelectedCraWayIds(waysWithNodes.map((way) => way.id));
            } else {
              setSelectedCraWayIds([]);
            }
          }}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>{t(`${i18prefix}.selectAll`)}</Checkbox.Label>
        </Checkbox.Root>
        <Separator />
        {waysWithNodes.map((way) => (
          <Checkbox.Root
            colorPalette="primary"
            key={way.id}
            name={way.id}
            checked={selectedCraWayIds.includes(way.id)}
            onCheckedChange={({ checked }) => {
              const craWayId = way.id;
              if (checked) {
                setSelectedCraWayIds((prev) => [...prev, craWayId]);
              } else {
                setSelectedCraWayIds((prev) =>
                  prev.filter((id) => id !== craWayId)
                );
              }
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>{`${selectedSurvey!.city.name}: ${
              way.name
            }`}</Checkbox.Label>
          </Checkbox.Root>
        ))}
      </Stack>
    </CustomDialog>
  );
};

export const CsvExportModal = observer(CsvExportModalBase);
